import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useArchivePatient } from '../state/data/patient';
import { useWithdrawPatient } from '../state/data/carePeriod';
import { useOpenWithdrawalRequest } from '../state/data/task';
import WithdrawalRequestPanel from './WithdrawalRequestPanel';
import { WITHDRAWAL_REQUEST_ON, requestCarePeriodId } from '../logic/withdrawalRequest';
import { LoadingPanel, Section, Callout } from './cms/cmsModalParts';
import { canWithdraw } from '../logic/offboarding';
import { CARE_PERIOD_STATUS_LABEL } from '../logic/carePeriod';
import {
  WITHDRAWAL_REASON_CODES, OTHER, NOTE_MAX,
  encodeWithdrawalReason, decodeWithdrawalReason,
} from '../logic/withdrawalReason';

// Two real sub-steps, in this order: archiving is what actually stops transmission,
// so it goes first and a failure aborts before anything is recorded.
const SUBMIT_LOADING_STEPS = [
  { at: 0, label: 'Archiving in RPM' },
  { at: 1200, label: 'Recording withdrawal' },
];

const ACKS = [
  'The patient will stop receiving all SMS, email, and chat messages from our services.',
  'The patient’s Withings devices will be unsubscribed; new readings will no longer flow in.',
  'The patient is archived in the RPM program and removed from the care-team board.',
  'The patient record is kept and this can be reversed by re-enrolling.',
];

// Why withdrawal is refused, and what the operator does instead. Rendered in the
// red (bad-tone) callout — the modal always explains the block rather than leaving a
// disabled button with a tooltip.
const BLOCKER_COPY = {
  cms_aligned: {
    headline: 'This patient is aligned with CMS',
    body: 'Withdrawing now would leave them aligned in the ACCESS Model with no care being delivered — Medicare would still consider them attributed to us. Unalign them in CMS first; once CMS confirms, withdrawal unlocks here.',
    cta: 'Unalign in CMS',
  },
  cms_unalignment_pending: {
    headline: 'CMS unalignment is still in progress',
    body: 'A $unalign request is in flight. Withdrawal unlocks once CMS returns the verdict and the care period leaves the pending state — usually within a minute. CMS can also refuse (for example during the mandatory lock-in period).',
    cta: null,
  },
  indeterminate: {
    headline: 'This patient can’t be withdrawn yet',
    body: 'Their care period isn’t in a state we can safely offboard from. This is a default-deny — check the care-period status before proceeding, or ask an engineer if it looks wrong.',
    cta: null,
  },
};

// WithdrawPatientModal — confirm → archive (Withings program) → fire
// patient_withdrawal_requested (WF67). No verdict poll on the withdrawal: WF67 writes no
// cms_submissions row. A patient CMS still holds gets WithdrawalRequestPanel instead.
// Spec: docs/specs/2026-07-17-patient-withdrawal-offboarding.md §A.
/**
 * @param {{ patient: { userid: string | number, [key: string]: any }, carePeriod: any, onClose: () => void, onSubmitted?: () => void,
 *   onRequestUnalign?: ((requestReasonLabel?: string | null) => void) | null, requestOn?: boolean,
 *   userRole?: string | null, actor?: object, onSaved?: (() => void) | null }} props
 */
export default function WithdrawPatientModal({
  patient, carePeriod, onClose, onSubmitted, onRequestUnalign = null,
  requestOn = WITHDRAWAL_REQUEST_ON, userRole = null, actor, onSaved = null,
}) {
  // 'confirm' → acknowledgements + confirm · 'submitting' → archive then event · 'done'
  const [phase, setPhase] = useState('confirm');
  const [checked, setChecked] = useState(() => ACKS.map(() => false));
  const [reasonCode, setReasonCode] = useState('');
  const [reasonNote, setReasonNote] = useState('');
  const [stepIndex, setStepIndex] = useState(0);
  const [submitError, setSubmitError] = useState(null);
  const aliveRef = useRef(true);
  // Survives a failed retry: archivePatient already succeeded, so re-firing the
  // whole sequence must NOT re-archive.
  const archivedRef = useRef(false);

  const archiveMut = useArchivePatient();
  const withdrawMut = useWithdrawPatient();

  // Re-run the gate here rather than trusting the caller: the modal is the last
  // place before an irreversible archive, so a stale drawer or a future call site
  // that skips the button check must still be refused.
  const gate = canWithdraw(carePeriod, { requestOn });
  const blocked = !gate.enabled;
  // Read whatever the flag says: an open request holds the patient, so it must stay visible and cancellable.
  const requestQuery = useOpenWithdrawalRequest(patient.userid, carePeriod?.care_period_id);
  const openRequest = requestQuery.data ?? null;
  // CMS still holds the patient: aligned, or an unalignment it has not answered with a request open.
  const unaligning = carePeriod?.status === 'pending_unalignment';
  // Latched while unaligning: a cancelled request must not swap the panel for the withdraw form mid-call.
  const [panelLatched, setPanelLatched] = useState(false);
  const wantsUnaligningPanel = unaligning && (!!openRequest || requestQuery.isLoading);
  if (wantsUnaligningPanel && !panelLatched) setPanelLatched(true);
  // Latched too: with requests switched off, a cancel or a failed read must not swap the panel for the block.
  const [alignedLatched, setAlignedLatched] = useState(false);
  const wantsAlignedPanel = carePeriod?.status === 'active' && (!!openRequest || requestQuery.isLoading || requestQuery.isError);
  if (wantsAlignedPanel && !alignedLatched) setAlignedLatched(true);
  const showRequestPanel = gate.mode === 'request' || wantsAlignedPanel || (carePeriod?.status === 'active' && alignedLatched)
    || wantsUnaligningPanel || (unaligning && panelLatched);
  // WF67 closes the requests of the period it withdraws; one left on another period stays open.
  const requestClosesHere = !!openRequest && requestCarePeriodId(openRequest) === String(carePeriod?.care_period_id ?? '');
  const blockerCopy = blocked ? (BLOCKER_COPY[gate.blocker] || BLOCKER_COPY.indeterminate) : null;

  const inFlight = phase === 'submitting';
  const allChecked = checked.every(Boolean);
  // WF24 writes the CMS unalignment code into the same column, and WF67 refuses to
  // overwrite it — so when one exists there is nothing to ask for.
  const priorReason = decodeWithdrawalReason(carePeriod?.disenrollment_reason);
  const cmsRecorded = priorReason?.kind === 'cms' ? priorReason : null;
  const needsNote = reasonCode === OTHER;
  // Ask the ENCODER, not `.trim()` — it also strips control characters, so a pasted
  // control-only note would enable the button and then send nothing at all.
  const reasonReady = cmsRecorded
    ? true
    : !!reasonCode && (!needsNote || encodeWithdrawalReason(reasonCode, reasonNote) !== null);

  function pickReason(code) {
    setReasonCode(code);
    // Never carry a note across codes: it would be encoded under the wrong one.
    if (code !== OTHER) setReasonNote('');
  }

  function toggleAck(i) {
    setChecked(prev => prev.map((v, idx) => (idx === i ? !v : v)));
  }

  async function handleSubmit() {
    // Gate here, not only on `disabled` — a future call site that skips the button
    // must still be refused.
    if (inFlight || !carePeriod || !allChecked || blocked || gate.mode !== 'withdraw' || !reasonReady) return;
    setSubmitError(null);
    setStepIndex(0);
    setPhase('submitting');

    if (!archivedRef.current) {
      try {
        await archiveMut.mutateAsync({ userid: patient.userid });
        archivedRef.current = true;
      } catch (e) {
        if (!aliveRef.current) return;
        setSubmitError(`Couldn’t archive the patient — nothing changed. ${e.message || ''}`.trim());
        setPhase('confirm');
        return;
      }
    }

    if (!aliveRef.current) return;
    setStepIndex(1);
    try {
      await withdrawMut.mutateAsync({
        withingsUserId: patient.userid,
        carePeriodId: carePeriod.care_period_id,
        withdrawalReason: cmsRecorded ? null : encodeWithdrawalReason(reasonCode, reasonNote),
      });
    } catch (e) {
      if (!aliveRef.current) return;
      setSubmitError(`Archived, but the withdrawal record failed — retry. ${e.message || ''}`.trim());
      setPhase('confirm');
      return;
    }
    if (!aliveRef.current) return;
    setPhase('done');
  }

  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, []);

  // Capture + stopPropagation: the patient drawer has its own window-level Escape
  // listener that navigates it closed, so a bubbling handler would dismiss this
  // modal AND throw the user back to the dashboard. Gated off mid-submit.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape' || inFlight) return;
      e.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose, inFlight]);

  const patientName = [
    patient.firstname || patient.first_name,
    patient.lastname || patient.last_name,
  ].filter(Boolean).join(' ').trim();

  return createPortal((
    <div className="modal-overlay" onClick={() => { if (!inFlight) onClose(); }}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 560, width: '100%', padding: 0, display: 'flex', flexDirection: 'column', maxHeight: '88vh' }}
      >
        <div style={{
          padding: '20px 24px 16px',
          borderBottom: '1px solid var(--wi-divider)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          gap: 12, flexShrink: 0,
        }}>
          <div style={{ minWidth: 0 }}>
            <h2 className="header3" style={{ margin: 0 }}>Withdraw patient</h2>
            <p className="detail1" style={{ margin: '4px 0 0', color: 'var(--wi-text-secondary)' }}>
              Reversible offboarding{patientName && <> · {patientName}</>}
            </p>
          </div>
          <button className="modal-close" aria-label="Close" onClick={onClose} disabled={inFlight} style={{ position: 'static' }}>×</button>
        </div>

        {showRequestPanel ? (
          <WithdrawalRequestPanel
            patient={patient}
            carePeriod={carePeriod}
            requestQuery={requestQuery}
            requestOn={requestOn}
            isCoordinator={userRole === 'care_coordinator'}
            actor={actor}
            onSaved={onSaved}
            onRequestUnalign={onRequestUnalign}
            onClose={onClose}
          />
        ) : (
        <>
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
          {phase === 'confirm' && blocked && blockerCopy && (
            <div>
              <Callout tone="bad">
                <p className="body2" style={{ margin: 0, fontWeight: 600, color: 'var(--wi-text-primary-bad)' }}>
                  {blockerCopy.headline}
                </p>
                <p className="body2" style={{ margin: '6px 0 0', color: 'var(--wi-text-primary-bad)' }}>
                  {blockerCopy.body}
                </p>
              </Callout>
              <Section label="Care period status">
                <p className="body2" style={{ margin: 0, color: 'var(--wi-text-primary)' }}>
                  {CARE_PERIOD_STATUS_LABEL[carePeriod?.status] || carePeriod?.status || '—'}
                </p>
              </Section>
            </div>
          )}

          {phase === 'confirm' && !blocked && (
            <div>
              <Callout tone="moderate">
                <p className="body2" style={{ margin: 0, color: 'var(--wi-text-primary-moderate)' }}>
                  This takes the patient out of the program without deleting anything. Their record and history are kept, and re-enrolling reverses it.
                </p>
                {openRequest && (
                  <p className="body2" style={{ margin: '6px 0 0', color: 'var(--wi-text-primary-moderate)' }}>
                    {requestClosesHere ? 'The withdrawal request will be closed.' : 'A withdrawal request stays open on another care period.'}
                  </p>
                )}
              </Callout>
              <Section label="Why is this patient leaving?">
                {cmsRecorded ? (
                  <div className="rx-field">
                    <label className="eyebrow" htmlFor="withdraw-reason-cms">Already recorded with CMS</label>
                    <input
                      id="withdraw-reason-cms"
                      className="rx-input"
                      readOnly
                      value={cmsRecorded.label || cmsRecorded.code}
                    />
                    <p className="detail1" style={{ color: 'var(--wi-text-secondary)', margin: 0 }}>
                      This came from the CMS unalignment and is the record of what we told Medicare, so it is kept as-is.
                    </p>
                  </div>
                ) : (
                  <>
                    {priorReason?.kind === 'legacy' && (
                      <Callout tone="moderate">
                        <p className="body2" style={{ margin: 0, color: 'var(--wi-text-primary-moderate)' }}>
                          A reason is already recorded and will be replaced: “{priorReason.note}”
                        </p>
                      </Callout>
                    )}
                    <div className="rx-field">
                      <label className="eyebrow" htmlFor="withdraw-reason">Reason (required)</label>
                      <select
                        id="withdraw-reason"
                        className="rx-select"
                        value={reasonCode}
                        onChange={(e) => pickReason(e.target.value)}
                      >
                        <option value="">Select a reason…</option>
                        {WITHDRAWAL_REASON_CODES.map(r => (
                          <option key={r.code} value={r.code}>{r.label}</option>
                        ))}
                      </select>
                    </div>
                    {needsNote && (
                      <div className="rx-field" style={{ marginTop: 12 }}>
                        <label className="eyebrow" htmlFor="withdraw-reason-note">What happened? (required)</label>
                        <textarea
                          id="withdraw-reason-note"
                          className="rx-input"
                          rows={2}
                          maxLength={NOTE_MAX}
                          value={reasonNote}
                          onChange={(e) => setReasonNote(e.target.value)}
                          placeholder="e.g. moved in with family out of state"
                        />
                        <p className="detail1" style={{ color: 'var(--wi-text-secondary)', margin: 0 }}>
                          Kept on the patient record. No clinical detail or identifiers.
                        </p>
                      </div>
                    )}
                  </>
                )}
              </Section>
              <Section label="Confirm you understand">
                <div role="group" aria-label="Confirm you understand" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {ACKS.map((text, i) => (
                    <label key={text} className="body2" style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', color: 'var(--wi-text-primary)' }}>
                      <input type="checkbox" checked={checked[i]} onChange={() => toggleAck(i)} style={{ marginTop: 3 }} />
                      <span>{text}</span>
                    </label>
                  ))}
                </div>
              </Section>
              {submitError && (
                <p className="detail1" style={{ margin: '12px 0 0', color: 'var(--wi-status-bad)' }}>{submitError}</p>
              )}
            </div>
          )}

          {phase === 'submitting' && (
            <LoadingPanel
              title="Withdrawing the patient…"
              stepIndex={stepIndex}
              steps={SUBMIT_LOADING_STEPS.map(s => s.label)}
              footnote="Device subscriptions are revoked in the background."
            />
          )}

          {phase === 'done' && (
            <Callout tone="good">
              <p className="body2" style={{ margin: 0, color: 'var(--wi-text-primary-good)' }}>
                The patient is archived and the withdrawal is recorded. The care period flips to Withdrawn and their devices are unsubscribed within a few seconds.
              </p>
            </Callout>
          )}
        </div>

        <div style={{
          display: 'flex', gap: 10, justifyContent: 'flex-end', alignItems: 'center',
          padding: '14px 24px', borderTop: '1px solid var(--wi-divider)', flexShrink: 0,
        }}>
          {phase === 'confirm' && blocked && (
            <>
              <button className="btn-secondary" onClick={onClose}>Close</button>
              {blockerCopy?.cta && onRequestUnalign && (
                <button className="btn-primary" onClick={() => onRequestUnalign()}>{blockerCopy.cta}</button>
              )}
            </>
          )}
          {phase === 'confirm' && !blocked && (
            <>
              <button className="btn-secondary" onClick={onClose}>Cancel</button>
              <button className="btn-primary" onClick={handleSubmit} disabled={!carePeriod || !allChecked || !reasonReady}>
                {archivedRef.current ? 'Retry withdrawal' : 'Withdraw patient'}
              </button>
            </>
          )}
          {phase === 'done' && (
            <button className="btn-primary" onClick={() => onSubmitted?.()}>Done</button>
          )}
        </div>
        </>
        )}
      </div>
    </div>
  ), document.body);
}
